import { Swiper, A11y, Navigation, Pagination, Scrollbar, Autoplay, Manipulation } from 'global-modules';

class SwiperSlider extends HTMLElement {
  constructor() {
    super();

    this.swiper = this.querySelector('[data-swiper]');

    // Stop when the swiper is not found
    if (!this.swiper) return;

    // Default to improve user experience
    this.swiperOptions = {
      observer: true,
      threshold: 10,
      showRecentProducts: false,
      modules: [A11y],
      destroyAfter: false,
      hasVideo: false,
      isVariantSlider: false,
    };

    // Check if we have extra options on the HTML
    if (this.dataset.options) {
      const options = JSON.parse(this.dataset.options);
      if (options) {
        this.swiperOptions = {
          ...this.swiperOptions,
          ...options,
        };
      }
    }

    if (this.swiperOptions.isVariantSlider) {
      this.swiperOptions = {
        ...this.swiperOptions,
        on: {
          sliderMove: function (swiper) {
            if (!swiper.el.nextElementSibling.classList.contains('product-card__variants-more')) return;

            if (swiper.isEnd) {
              swiper.el.nextElementSibling.classList.add('hidden');
            }
            else {
              swiper.el.nextElementSibling.classList.remove('hidden');
            }
          },
        },
      };
    }

    // Load the Navigation, Pagination, Scrollbar, Autoplay modules as needed
    if (this.swiperOptions.pagination) this.swiperOptions.modules.push(Pagination);
    if (this.swiperOptions.navigation) this.swiperOptions.modules.push(Navigation);
    if (this.swiperOptions.scrollbar) this.swiperOptions.modules.push(Scrollbar);
    if (this.swiperOptions.autoplay) this.swiperOptions.modules.push(Autoplay);
    if (this.swiperOptions.showRecentProducts) this.swiperOptions.modules.push(Manipulation);

    if (this.swiperOptions.destroyAfter) {
      window.addEventListener('window:resize', () => {
        if (window.windowWidth >= this.swiperOptions.destroyAfter && this.swiperInstance) {
          this.swiperInstance.destroy();
        }
        else {
          this.init();
        }
      });

      window.addEventListener('DOMContentLoaded', () => {
        if (window.windowWidth >= this.swiperOptions.destroyAfter && this.swiperInstance) {
          this.swiperInstance.destroy();
        }
        else if (window.windowWidth < this.swiperOptions.destroyAfter) {
          this.init();
        }
      });
    }
    else {
      this.init();

      if (this.querySelector('[data-swiper-init]')) {
        this.calculateLeftOverSlides();

        window.addEventListener('window:resize', this.calculateLeftOverSlides.bind(this));
      }
    }

    // When the swiper has video
    if (this.swiperOptions.hasVideo) {
      this.debouncedOnLoad = debounce(event => {
        this.swiperObserver(event.type);
      }, 100);

      // check if the swiper is visible
      window.addEventListener('load', this.debouncedOnLoad.bind(this));

      window.addEventListener('scroll', this.debouncedOnLoad.bind(this));

      // when the swiper contains a video, pause it when it's swiped out and play it if it's swiped into view and has autoplay enabled
      this.swiperInstance.on('slideChangeTransitionEnd', swiper => {
        const previousSlide = swiper.slides[swiper.previousIndex];
        const previousSlideType = previousSlide.dataset.swiperVideoType;
        const previousVideo = previousSlide.querySelector(previousSlideType);

        const currentSlide = swiper.slides[swiper.activeIndex];
        const currentSlideType = currentSlide.dataset.swiperVideoType;
        const currentVideo = currentSlide.querySelector(currentSlideType);

        if (previousVideo && !this.getPausedState(previousVideo, previousSlideType)) previousVideo.pauseVideo();
        if (currentVideo && currentVideo.options.autoplay && !this.getPausedState(currentVideo, currentSlideType) && currentVideo.dataset.videoIsPaused !== 'true') currentVideo.playVideo();
      });
    }

    // Listen to extra events when in Shopify editor
    if (Shopify.designMode) {
      // Update the swiper when the section event is triggered
      window.addEventListener('shopify:section:load', event => this.init(event, true));

      // When on block select go to the slide in front-end
      window.addEventListener('shopify:block:select', event => this.handleBlockSelect(event));

      // When on block deselect go to the slide in front-end
      window.addEventListener('shopify:block:deselect', event => this.handleBlockSelect(event));
    }
  }

  calculateLeftOverSlides() {
    if (!this.swiperInstance) return;

    const initButton = this.querySelector('[data-swiper-init]');
    const leftOverWidth = this.swiperInstance.virtualSize - this.swiperInstance.width;
    const leftOverSlides = Math.ceil(leftOverWidth / this.swiperInstance.slides[0]?.offsetWidth) + 1 || 0;

    if (leftOverSlides > 1) {
      initButton.querySelector('[data-swiper-init-text]').innerHTML = `+${leftOverSlides}`;
      initButton.addEventListener('click', () => {
        initButton.classList.add('hidden');
      });
    }
    else {
      initButton.classList.add('hidden');
    }
  }

  getPausedState(video, type) {
    switch (type) {
    case 'youtube-video':
      return video.getPausedState();
    case 'vimeo-video':
      return video.paused;
    case 'custom-video':
      return video.videoElement.paused;
    }
  }

  swiperObserver() {
    if (!this.swiperInstance) return;

    // Register IntersectionObserver
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const elementBounds = entry.boundingClientRect;
        const isBottomVisible = !!(elementBounds.bottom < window.windowHeight) && elementBounds.bottom;
        const isTopVisible = !!(elementBounds.top > 0) && elementBounds.top;

        const swiper = this.swiperInstance;
        const currentSlide = swiper.slides[swiper.activeIndex];
        const currentSlideType = currentSlide.dataset.swiperVideoType;
        const currentVideo = currentSlide.querySelector(currentSlideType);

        if ((isBottomVisible && isTopVisible) || (entry.isIntersecting && entry.intersectionRatio > 0.9)) {
          if (currentVideo && currentVideo.options.autoplay) currentVideo.playVideo();
        }
        else if (!this.getPausedState(currentVideo, currentSlideType)) currentVideo.pauseVideo();
      });
    });
    observer.observe(this);
  }

  init(event = null, updateSwiper = false) {
    if (this.swiperOptions.showRecentProducts) {
      // if this should be the swiper for 'recently viewed items' but the local storage is empty, hide the swiper
      if (window.localStorage.getItem('visited-products') === null) {
        this.closest('.viewed-products-swiper').classList.add('hidden');
      }
      else {
        // Render the 'recently viewed items'
        this.getProducts();
      }
    }
    else {
      // only used for the Theme Editor to check if the swiper matches the section that was updated
      if (updateSwiper && event.detail.sectionId !== this.swiperOptions.sectionID) return;

      // Check if we have extra options on the HTML
      if (this.dataset.options) {
        const options = JSON.parse(this.dataset.options);
        if (options) {
          this.swiperOptions = {
            ...this.swiperOptions,
            ...options,
          };
        }
      }

      if (this.swiperOptions.destroyAfter) {
        const ww = window.windowWidth;
        if (ww >= this.swiperOptions.destroyAfter && this.swiperInstance) {
          this.swiperInstance.destroy();
          return;
        }
      }

      if (updateSwiper) {
        this.updateSwiper();
      }
      else {
        // Call swiper with selected swiper element and options
        this.swiperInstance = new Swiper(this.swiper, this.swiperOptions);
      }
    }
  }

  async getProducts() {
    this.productsArray = JSON.parse(window.localStorage.getItem('visited-products')).reverse();

    // cut the array to the max amount of slides
    if (this.productsArray.length > this.swiperOptions.maxCount) {
      this.productsArray.length = this.swiperOptions.maxCount;
    }

    // Init the empty swiper
    this.swiperInstance = new Swiper(this.swiper, this.swiperOptions);

    // use map instead of forEach because async/await won't work. Product string includes /product/ or even collections/collection_handle/product/
    const promises = this.productsArray.map(async product => {
      await fetch(`${product}?section_id=theme-product-card`)
        .then(response => response.text())
        .then(responseText => {
          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const productHTML = parsedHTML.getElementById('product-card-slide');

          if (productHTML) {
            this.swiperInstance.appendSlide(productHTML);
          }
        })
        .catch(error => {
          debug() && console.log(error);
        });
    });

    // update the elements when all items have been processed
    await Promise.all(promises).then(() => {
      this.updateSwiper(true);
    });
  }

  updateSwiper(update = false) {
    this.swiperInstance.update();

    if (this.swiperOptions.navigation?.nextEl) {
      this.swiperInstance.navigation.init();
      if (update) this.swiperInstance.navigation.update();
    }
    if (this.swiperOptions.pagination?.el) {
      this.swiperInstance.pagination.init();
      this.swiperInstance.pagination.render();
      if (update) this.swiperInstance.pagination.update();
    }
    // SCROLLBAR doesn't update in the theme editor. This seems to be a bug in swiper.
    if (this.swiperOptions.scrollbar?.el) {
      this.swiperInstance.scrollbar.init();
      if (update) this.swiperInstance.scrollbar.updateSize();
      if (update) this.swiperInstance.scrollbar.setTranslate();
    }
  }

  /**
   * Handles the theme editor block change/edit event
   * @param {Object} event
   */
  handleBlockSelect(event) {
    if (event.detail.sectionId !== this.swiperOptions.sectionID) return;

    // Check if the slide index is set
    if (!('swiperSlideIndex' in event.target.dataset)) return;

    // Set the slide index based on loop settings or not
    let swipeToSlideIndex = +event.target.dataset.swiperSlideIndex;

    // Slide to slide based on the data attribute from the target
    const sliderToUpdate = event.target.closest('[data-swiper]');

    sliderToUpdate.swiper.slideTo(swipeToSlideIndex, 500, false);
  }
}

if (!customElements.get('swiper-slider')) {
  customElements.define('swiper-slider', SwiperSlider);
}

class ProductRecommendations extends SwiperSlider {
  constructor() {
    super();

    this.swiperOptions = {
      ...this.swiperOptions,
      slider: true,
    };

    if (this.querySelector('swiper-slider')) {
      const dataOptions = this.querySelector('swiper-slider').dataset.options;
      if (dataOptions) {
        const options = JSON.parse(dataOptions);
        if (options) {
          this.swiperOptions = {
            ...this.swiperOptions,
            ...options,
          };
        }
      }
    }
    this.init();
  }

  init() {
    if ('url' in this.dataset) {
      fetch(this.dataset.url)
        .then(response => response.text())
        .then(text => {
          const html = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-recommended-products]').innerHTML;
          this.querySelector('[data-recommended-products]').innerHTML = html;

          if (this.swiperOptions.slider) {
            this.swiperInstance = new Swiper(this.swiper, this.swiperOptions);
          }
        })
        .catch(event => {
          debug() && console.error(event);
        });
    }
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
